import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, FileCheck2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { depositStatus, uploadDeposit } from "@/lib/depotApi";

const T = {
  fr: {
    title: "Déposer mon carnet",
    intro: "Votre praticien vous a transmis ce lien pour recevoir votre carnet. Déposez ici le fichier PDF que vous avez exporté depuis le module.",
    pick: "Choisir le fichier PDF",
    selected: "Fichier sélectionné :",
    send: "Envoyer mon carnet",
    sending: "Envoi…",
    onlyPdf: "Merci de choisir un fichier PDF.",
    tooBig: "Fichier trop volumineux (max 20 Mo).",
    successTitle: "Carnet envoyé !",
    successText: "Votre carnet a bien été transmis à votre praticien. Vous pouvez maintenant fermer cette page.",
    invalidTitle: "Lien invalide",
    invalidText: "Ce lien n'est pas valide. Merci de redemander un lien à votre praticien.",
    expiredTitle: "Lien expiré",
    expiredText: "Ce lien de dépôt a expiré. Merci de demander un nouveau lien à votre praticien.",
    loading: "Chargement…",
    error: "Échec de l'envoi — réessayez.",
    replaceNote: "Un carnet a déjà été déposé. Vous pouvez le remplacer en envoyant un nouveau fichier.",
  },
  es: {
    title: "Depositar mi cuaderno",
    intro: "Su profesional le ha enviado este enlace para recibir su cuaderno. Deposite aquí el archivo PDF que exportó desde el módulo.",
    pick: "Elegir el archivo PDF",
    selected: "Archivo seleccionado:",
    send: "Enviar mi cuaderno",
    sending: "Enviando…",
    onlyPdf: "Por favor, elija un archivo PDF.",
    tooBig: "Archivo demasiado grande (máx. 20 MB).",
    successTitle: "¡Cuaderno enviado!",
    successText: "Su cuaderno se ha enviado correctamente a su profesional. Ahora puede cerrar esta página.",
    invalidTitle: "Enlace no válido",
    invalidText: "Este enlace no es válido. Solicite un nuevo enlace a su profesional.",
    expiredTitle: "Enlace caducado",
    expiredText: "Este enlace de depósito ha caducado. Solicite un nuevo enlace a su profesional.",
    loading: "Cargando…",
    error: "Error al enviar — inténtelo de nuevo.",
    replaceNote: "Ya se depositó un cuaderno. Puede reemplazarlo enviando un nuevo archivo.",
  },
};

export default function DepotUpload() {
  const { token } = useParams();
  const [lang, setLang] = useState("fr");
  const [phase, setPhase] = useState("loading"); // loading | invalid | expired | ready | done
  const [already, setAlready] = useState(false);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  const t = T[lang];

  useEffect(() => {
    let active = true;
    depositStatus(token).then((d) => {
      if (!active) return;
      if (!d.found) setPhase("invalid");
      else if (d.expired) setPhase("expired");
      else { setPhase("ready"); setAlready(d.status === "received"); }
    }).catch(() => { if (active) setPhase("invalid"); });
    return () => { active = false; };
  }, [token]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) { toast.error(t.onlyPdf); return; }
    if (f.size > 20 * 1024 * 1024) { toast.error(t.tooBig); return; }
    setFile(f);
  };

  const onSend = async () => {
    if (!file) return;
    setSending(true);
    try { await uploadDeposit(token, file); setPhase("done"); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) {
      const code = e?.response?.status;
      if (code === 410) setPhase("expired");
      else if (code === 404) setPhase("invalid");
      else toast.error(t.error);
    }
    setSending(false);
  };

  const render = () => {
    if (phase === "loading") return <p className="text-center py-20 text-[var(--ink-soft)]" data-testid="depot-loading">{t.loading}</p>;
    if (phase === "invalid") return (
      <div className="text-center py-20 animate-fade-up" data-testid="depot-invalid">
        <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-3">{t.invalidTitle}</h1>
        <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">{t.invalidText}</p>
      </div>
    );
    if (phase === "expired") return (
      <div className="text-center py-20 animate-fade-up" data-testid="depot-expired">
        <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-3">{t.expiredTitle}</h1>
        <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">{t.expiredText}</p>
      </div>
    );
    if (phase === "done") return (
      <div className="text-center py-10 animate-fade-up" data-testid="depot-done">
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12 max-w-lg mx-auto">
          <FileCheck2 size={44} className="mx-auto mb-4 text-[var(--gold)]" />
          <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-3">{t.successTitle}</h2>
          <p className="text-[var(--ink)] leading-relaxed">{t.successText}</p>
        </div>
      </div>
    );
    return (
      <div className="max-w-lg mx-auto animate-fade-up text-center" data-testid="depot-ready">
        <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-5">{t.title}</h1>
        <p className="text-[var(--ink)] leading-relaxed mb-8">{t.intro}</p>
        {already && <p className="text-xs text-[var(--ink-soft)] mb-4">{t.replaceNote}</p>}
        <div className="rounded-3xl bg-[var(--sand)] border-2 border-dashed border-[var(--gold-light)] p-8 mb-6">
          <UploadCloud size={40} className="mx-auto mb-4 text-[var(--gold)]" />
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={onPick} data-testid="depot-file-input" className="hidden" />
          <button onClick={() => inputRef.current?.click()} data-testid="depot-pick-btn"
            className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.pick}</button>
          {file && <p className="text-sm text-[var(--ink)] mt-4" data-testid="depot-file-name">{t.selected} <strong>{file.name}</strong></p>}
        </div>
        <button onClick={onSend} disabled={!file || sending} data-testid="depot-send-btn"
          className={`px-8 py-3.5 rounded-full font-medium shadow-sm transition-all ${file && !sending ? "bg-[var(--gold)] text-white hover:brightness-105" : "bg-[var(--beige)] text-[var(--ink-soft)] cursor-not-allowed"}`}>
          {sending ? t.sending : t.send}
        </button>
      </div>
    );
  };

  return (
    <Layout>
      <div className="flex justify-end mb-4">
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      {render()}
    </Layout>
  );
}
